import React from "react";
import moment from "moment-timezone";
import { Box, Group, Image, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconAlertCircle, IconCheck, IconClock } from "@tabler/icons-react";
import type { Message } from "../../types/messaging";
import {
  outboundStatusLabel,
  outboundStatusCategory,
  describeMessageError,
  stripOutboundBusinessPrefix,
} from "./chatHelpers";
import styles from "./MessageBubble.module.css";

/** One check for both sent + delivered, a clock while pending, alert on failure. */
const STATUS_ICONS = {
  pending: IconClock,
  sent: IconCheck,
  failed: IconAlertCircle,
} as const;

interface MessageBubbleProps {
  message: Message;
  /** Whether to show the time/status meta line under the bubble. */
  showMeta?: boolean;
  /** Open an attachment full-size — enables tap-to-expand on the thumbnail. */
  onOpenImage?: (url: string) => void;
  /** Business name(s) whose leading "[Name] " tag is stripped from outbound bodies. */
  businessName?: string;
  legalBusinessName?: string;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  showMeta = true,
  onOpenImage,
  businessName,
  legalBusinessName,
}) => {
  const outbound = message.direction === "outbound";
  // Outbound SMS carry a "[Salon] " sender tag from the backend — hide it here.
  const body = outbound
    ? stripOutboundBusinessPrefix(message.body, [
        businessName,
        legalBusinessName,
      ])
    : message.body;
  // created_dt is a naive UTC string from the API — parse as UTC and display in
  // the viewer's local timezone.
  const time = moment.utc(message.created_dt).local().format("h:mm A");
  const category = outboundStatusCategory(message);
  const failed = category === "failed";
  const StatusIcon = category ? STATUS_ICONS[category] : null;
  // Failed sends surface the (staff-friendly) reason for the Twilio error code.
  const errorDetail = failed ? describeMessageError(message.error_code) : "";
  const statusLabel = outboundStatusLabel(message);
  const statusText = errorDetail
    ? `${statusLabel} — ${errorDetail}`
    : statusLabel;
  // Collapsed clusters show one meta line on the tail (showMeta), but a failed
  // send always surfaces its own status so a mid-cluster failure is never hidden.
  const showMetaLine = showMeta || failed;

  return (
    <Box className={`${styles.row} ${outbound ? styles.rowOut : styles.rowIn}`}>
      <Stack gap={2} className={styles.stack}>
        <Box
          className={`${styles.bubble} ${
            outbound ? styles.bubbleOut : styles.bubbleIn
          }`}
        >
          {message.media.length > 0 && (
            <Stack gap={6} mb={message.body ? 6 : 0}>
              {message.media.map((m, i) => {
                if (!m.url) return null;
                const url = m.url;
                const thumb = (
                  <Image src={url} alt="Attachment" className={styles.media} />
                );
                // Tap-to-expand when a handler is wired; otherwise a plain,
                // still-capped thumbnail.
                return onOpenImage ? (
                  <UnstyledButton
                    key={i}
                    className={styles.mediaButton}
                    aria-label="View attachment full size"
                    onClick={() => onOpenImage(url)}
                  >
                    {thumb}
                  </UnstyledButton>
                ) : (
                  <Box key={i}>{thumb}</Box>
                );
              })}
            </Stack>
          )}
          {body && (
            <Text className={styles.body} component="span">
              {body}
            </Text>
          )}
        </Box>
        {showMetaLine && (
          <Group
            gap={6}
            className={styles.meta}
            justify={outbound ? "flex-end" : "flex-start"}
          >
            <Text component="span" className={styles.metaText}>
              {time}
            </Text>
            {StatusIcon && statusLabel && (
              <Group
                gap={3}
                align="center"
                wrap="nowrap"
                className={`${styles.status} ${failed ? styles.statusError : ""}`}
              >
                <StatusIcon size={13} stroke={2} aria-hidden />
                <Text component="span" className={styles.statusText}>
                  {statusText}
                </Text>
              </Group>
            )}
          </Group>
        )}
      </Stack>
    </Box>
  );
};

export default MessageBubble;
